import React from 'react';
import AchievementsOrb from '../AchievementsOrb/AchievementsOrb';
import { GameTipPanel, GameTipHost } from '../GameTip';
import './PlayerTray.css';
import './NavOrbTile.css';

/**
 * PlayerTray — bottom-left of the map viewport.
 *
 * Navigation widgets, left→right: Drug Lab, Skill-Tree, Drug Bank, Concept Vault.
 * The Drug Lab tile moved here 2026-06-20 (Edward): it used to be an orb in the
 * bottom-right MapOrbTray carousel (`DrugLabOrb`); it now reads as a bottom-left
 * nav tile alongside the Bank/Vault/Skill-Tree tools. `drugLabActive` drives the
 * amber active highlight while the DrugLabMap is open. Reordered 2026-06-21
 * (Edward) so Drug Lab leads, followed by Skill-Tree, then Bank and Vault.
 * The Wholesaler (Shop) and Player Stats chips were preprod-only
 * experimental tiles; they were removed 2026-06-20 (Edward). Dashboard still
 * passes onShop/onStats (harmlessly ignored) so re-adding them later needs no
 * Dashboard change.
 * (The former Pharmacy widget was removed when /player/pharmacy was
 * deprecated — the Drug Bank widget is the canonical Pharmacy modal.)
 *
 * Tiles render as 44×44 rounded-square widgets via `.pt-orb` (kept for
 * CSS continuity even though the visual is no longer orb-shaped).
 */
export default function PlayerTray({
  onBank,
  onVault,
  onDrugLab,
  drugLabActive = false,
  onSkillTree,
  skillTreeActive = false,
  skillTreeOnly = false,
  betaMode = false,
  hpAnchored = false,
  skillTreeHint = false,
  onRandomLoadout,
  randomLoadoutBusy = false,
  gameOrbs = null,
}) {
  // No-signup landing: render ONLY the skill-tree toggle (AchievementsOrb) in its
  // exact dashboard position — the Bank/Vault/Shop/Stats nav orbs all bounce anon
  // visitors to login, so they're gated out (Edward 2026-06-07).
  if (skillTreeOnly) {
    // Skill-tree orb IS the free-equip affordance on the HTN standalone. Gated to
    // beta (pre-pharm) only (Edward 2026-07-20): normal players progress the map +
    // acquire via blister packs and must NOT get the orb / free-equip. Hidden
    // entirely for them — if nothing is left to render, drop the tray so no empty
    // box shows in the corner.
    const showSkillOrb = betaMode;
    if (!showSkillOrb && !onRandomLoadout) return null;
    return (
      <div className={`player-tray player-tray--skill-only${hpAnchored ? ' player-tray--hp-anchored' : ''}`}>
        <div className="player-tray-row">
          {showSkillOrb && (
            <AchievementsOrb active={skillTreeActive} onActivate={onSkillTree} hint={skillTreeHint} />
          )}
          {onRandomLoadout && (
            <GameTipHost
              as="button"
              type="button"
              className="pt-random-loadout combat-tt-host"
              onClick={onRandomLoadout}
              disabled={randomLoadoutBusy}
              aria-label="Equip a random loadout"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M16 3h5v5" />
                <path d="M4 20L21 3" />
                <path d="M21 16v5h-5" />
                <path d="M15 15l6 6" />
                <path d="M4 4l5 5" />
              </svg>
              <span>{randomLoadoutBusy ? 'Rolling…' : 'Random Loadout'}</span>
              <GameTipPanel
                placement="above-left"
                name="Random Loadout"
                sections={[{
                  lines: [
                    'equip random keystone + 2 augments',
                    { stats: [{ g: 'tap' }, { j: '·' }, { t: 'roll loadout' }] },
                  ],
                }]}
              />
            </GameTipHost>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="player-tray">
      {/* Single inline row (Edward 2026-07-20, order INVERTED per Edward): every
          button shares one row and one vertical baseline. The tools group (Drug
          Lab / Skill-Tree / Bank / Vault) sits LEFT of a subtle VERTICAL divider,
          then the game/social nav tiles (migrated 2026-07-19 from the retired
          bottom-right MapOrbTray carousel, each keeping its old orb color at
          baseline, see NavOrbTile.css) on the RIGHT. The divider only renders when
          there are orbs present to separate. */}
      <div className="player-tray-row">
        {/* Order (Edward 2026-06-21): Drug Lab leftmost, then Skill Tree,
            then Drug Bank and Concept Vault. The Drug Lab tile is now the
            primary leftmost nav widget. */}
        <button
          className={`pt-orb${drugLabActive ? ' pt-orb--active' : ''}`}
          onClick={onDrugLab}
          aria-label={drugLabActive ? 'Exit Drug Lab' : 'Open Drug Lab'}
          aria-pressed={drugLabActive}
        >
          <span className="pt-orb-ring" />
          {/* Erlenmeyer flask — the drug-lab motif, carried over from the
              retired amber DrugLabOrb so the surface stays recognizable. */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 3h6" />
            <path d="M10 3v5.2L4.8 17.4A1.6 1.6 0 0 0 6.2 20h11.6a1.6 1.6 0 0 0 1.4-2.6L14 8.2V3" />
            <path d="M7.4 14h9.2" />
            <circle cx="10.5" cy="16.5" r="0.7" fill="currentColor" stroke="none" />
            <circle cx="13.4" cy="17.6" r="0.6" fill="currentColor" stroke="none" />
          </svg>
        </button>

        <AchievementsOrb active={skillTreeActive} onActivate={onSkillTree} />

        <button className="pt-orb" onClick={onBank} aria-label="Drug Bank">
          <span className="pt-orb-ring" />
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 01-8 0" />
          </svg>
        </button>

        <button className="pt-orb" onClick={onVault} aria-label="Concept Vault">
          <span className="pt-orb-ring" />
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" />
            <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
            <line x1="12" y1="12" x2="12" y2="16" />
            <line x1="10" y1="14" x2="14" y2="14" />
          </svg>
        </button>

        {/* Game/social orbs now on the RIGHT of the divider (Edward inverted the
            order 2026-07-20). Divider renders only when orbs are present. */}
        {gameOrbs && <div className="player-tray-divider" aria-hidden="true" />}
        {gameOrbs}
      </div>
    </div>
  );
}
